import { useEffect, useId, useState } from 'react';
import { X } from 'lucide-react';
import { useSetAtom } from 'jotai';
import { ObservableLevelEnumValue } from '../../data/enums';
import { observationService } from '../../services/observationService';
import { cacheInvalidationService } from '../../services/cacheInvalidationService';
import { inspectionDB } from '../../data/database.structure';
import { Label } from './Label';
import {
  ActionTypeStore,
  actionTypeStore,
} from '../../store/inspection/observation/actionTypeStore';
import { Tooltip } from './Tooltip';
import { ObservableItemIdentifier } from '../../api-contracts/other/ObservableItemIdentifier';
import WearDisplayHelpers from '../../helpers/wearDisplayHelpers';
import { PartDimensionModel } from '../../data/types';
import { WarningBanner } from './WarningBanner';
import { CustomDialogBox } from '../molecules/CustomDialogBox';
import {
  cancelPendingNavigationAtom,
  proceedPendingNavigationAtom,
  registerNavigationBlockerAtom,
  unregisterNavigationBlockerAtom,
} from '../../store/navigation/navigationBlockerAtom';

interface Measurement {
  value: number;
  id: string;
}

interface PitchGaugeComponentProps {
  path: Record<string, string> & {
    Measurement: string;
    CalculatedPitchGauge: string;
  };
  // path: string;
  // childPath: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  inspectionCategoryTypeId: string;
  onChange?: (value: string) => void;
  onDefaultLoad?: (value: string[]) => void;
}

export const PitchGaugeWornLengthComponent = ({
  inspectionId,
  inspectionCategoryTypeId,
  path,
  // dataLevel,
  // entityId,
  // childPath,
  dataLevel,
  entityId,
  entityExternalId,
  //   onChange,
  onDefaultLoad,
}: PitchGaugeComponentProps) => {
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [currentValue, setCurrentValue] = useState<string>('');
  const [error, setError] = useState<string | null>('');
  // DISPLAY-ONLY (#6032): baseline/limit for showing wear extension instead of
  // the raw absolute average. Stored observation values stay RAW.
  const [partDimension, setPartDimension] = useState<
    PartDimensionModel | undefined
  >(undefined);
  // #10668: a reading typed into the entry field is not an observation until
  // Add measurement commits it. Leaving the screen with one still in the field
  // used to discard it silently, so every route change now stops on this
  // dialog (see the navigation blocker registered below).
  const [isLeaveDialogOpen, setLeaveDialogOpen] = useState(false);

  const newReadingInputId = useId();
  const registerNavigationBlocker = useSetAtom(registerNavigationBlockerAtom);
  const unregisterNavigationBlocker = useSetAtom(
    unregisterNavigationBlockerAtom,
  );
  const proceedPendingNavigation = useSetAtom(proceedPendingNavigationAtom);
  const cancelPendingNavigation = useSetAtom(cancelPendingNavigationAtom);

  const pendingValue = currentValue.trim();
  const pendingNumber = pendingValue === '' ? NaN : parseFloat(pendingValue);
  const hasPendingReading = pendingValue !== '';

  // Load the part dimension (baseline/limit) for the calculated pitch gauge
  useEffect(() => {
    const loadPartDimension = async () => {
      const observableItem = await inspectionDB.observableItems
        .where('identifier')
        .equals(
          ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
            .PitchExtension.PitchGauge.CalculatedPitchGauge,
        )
        .first();
      if (!observableItem) return;

      const dimension = await actionTypeStore.getPartDimension({
        equipmentPartId: entityExternalId,
        observableItemId: observableItem.id,
      });
      setPartDimension(dimension);
    };

    void loadPartDimension();
  }, []);

  // Load Default
  useEffect(() => {
    const getDefaultValue = async () => {
      const inspection = await inspectionDB.inspections.get(inspectionId);

      if (!inspection) {
        setError('Inspection not found');
        return;
      }

      const inspectionCategoryType = inspection.inspectionCategoryTypes.find(
        (i) => i.externalId === inspectionCategoryTypeId,
      );

      if (!inspectionCategoryType) {
        setError('Inspection Category Type not found');
        return;
      }

      setError(null);
      const observations = await observationService.getObservations<number>({
        dataLevel: dataLevel,
        entityId: entityId,
        entityExternalId,
        observations: inspectionCategoryType.observations,
        path: path.Measurement,
      });

      // setObservations(observations);
      const observationMeasurements = observations.map((o) => ({
        value: parseFloat(o.value),
        id: o.externalId.toString(),
      })) as Measurement[];

      setMeasurements(observationMeasurements);

      onDefaultLoad?.(observationMeasurements.map((m) => m.value.toString()));
    };

    void getDefaultValue();
  }, []);

  // #10668: guard every way off this screen while a reading sits uncommitted
  // in the entry field. Layout's useNavigationGuard enforces this registration
  // at the router, so Back, the browser back gesture, Drawer links and Complete
  // all reach it. Registering per-value keeps the blocker's closure reading the
  // current entry, and unregistering on cleanup means an empty field never
  // blocks anything.
  useEffect(() => {
    const blockerId = `pitch-gauge-pending-reading-${entityExternalId}`;

    if (!hasPendingReading) {
      unregisterNavigationBlocker(blockerId);
      return;
    }

    registerNavigationBlocker({
      id: blockerId,
      blocker: () => {
        setLeaveDialogOpen(true);
        return true; // Block navigation until the inspector decides
      },
      // A reload or tab close would take the typed reading with it, and no
      // router guard can see those - so arm the browser's own prompt too.
      warnOnUnload: true,
    });

    return () => {
      unregisterNavigationBlocker(blockerId);
    };
  }, [
    entityExternalId,
    hasPendingReading,
    registerNavigationBlocker,
    unregisterNavigationBlocker,
  ]);

  const handleAddMeasurement = async () => {
    if (!currentValue.trim()) {
      setError('invalid value');
      return;
    }

    const numValue = parseFloat(currentValue);
    if (isNaN(numValue)) {
      setError('invalid value');
      return;
    }

    const localMeasurements = [
      ...measurements,
      { value: numValue, id: crypto.randomUUID() },
    ];

    const average = calculateAverage(localMeasurements);

    const observableItem = await inspectionDB.observableItems
      .where('identifier')
      .equals(
        ObservableItemIdentifier.Identifiers.Undercarriage.Shoes.PitchExtension
          .PitchGauge.CalculatedPitchGauge,
      )
      .first();
    if (!observableItem) {
      setError('Unable to validate measurement — part configuration not found');
      return;
    }

    const actionStatus = await new ActionTypeStore().getPartActionStatus({
      equipmentPartId: entityExternalId,
      value: parseFloat(average),
      observableItemId: observableItem.id,
    });
    const isValid = ActionTypeStore.validateActionStatus({
      actionStatus: actionStatus,
    });

    if (
      !isValid &&
      (actionStatus.percentage > 250 || actionStatus.percentage < 0)
    ) {
      // #6032: never reject silently — tell the inspector what range applies
      setError(
        WearDisplayHelpers.formatOutOfRangeError({
          value: parseFloat(average),
          minValue: actionStatus.minValue,
          maxValue: actionStatus.maxValue,
        }),
      );
      return;
    }

    const newObservation = {
      dataLevel: dataLevel,
      entityId: entityId,
      entityExternalId,
      inspectionCategoryId: inspectionCategoryTypeId,
      inspectionId: inspectionId,
      path: path.Measurement,
      value: numValue.toString(),
      unitId: 15,
    };

    await observationService.upsertObservation(newObservation);

    await observationService.upsertObservation({
      dataLevel: dataLevel,
      entityId: entityId,
      entityExternalId,
      inspectionCategoryId: inspectionCategoryTypeId,
      inspectionId: inspectionId,
      path: path.CalculatedPitchGauge,
      value: average,
      unitId: 15,
    });

    // The Action Status bar reads observations from the cached inspection query;
    // direct observationService writes bypass the mutation hooks, so without this
    // the new CalculatedPitchGauge never reaches the worst-case status (#10549).
    await cacheInvalidationService.invalidateInspectionAfterObservationChangeImmediate(
      inspectionId,
    );

    setCurrentValue('');
    setMeasurements(localMeasurements);
    setError('');
  };

  const handleRemoveMeasurement = async (id: string) => {
    const filteredMeasurements = measurements.filter((m) => m.id !== id);

    if (filteredMeasurements.length > 0) {
      const average = calculateAverage(filteredMeasurements);

      const observableItem = await inspectionDB.observableItems
        .where('identifier')
        .equals(
          ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
            .PitchExtension.PitchGauge.CalculatedPitchGauge,
        )
        .first();
      if (!observableItem) {
        setError(
          'Unable to validate measurement — part configuration not found',
        );
        return;
      }

      const actionStatus = await new ActionTypeStore().getPartActionStatus({
        equipmentPartId: entityExternalId,
        value: parseFloat(average),
        observableItemId: observableItem.id,
      });
      const isValid = ActionTypeStore.validateActionStatus({
        actionStatus: actionStatus,
      });

      if (
        !isValid &&
        (actionStatus.percentage > 250 || actionStatus.percentage < 0)
      ) {
        // #6032: never reject silently — tell the inspector what range applies
        setError(
          WearDisplayHelpers.formatOutOfRangeError({
            value: parseFloat(average),
            minValue: actionStatus.minValue,
            maxValue: actionStatus.maxValue,
          }),
        );
        return;
      }
    }

    await observationService.deleteObservationsByProps({
      dataLevel: dataLevel,
      entityId: entityId,
      entityExternalId,
      inspectionCategoryId: inspectionCategoryTypeId,
      inspectionId: inspectionId,
      path: path.Measurement,
      unitId: 15,
    });

    await observationService.deleteObservationsByProps({
      dataLevel: dataLevel,
      entityId: entityId,
      entityExternalId,
      inspectionCategoryId: inspectionCategoryTypeId,
      inspectionId: inspectionId,
      path: path.CalculatedPitchGauge,
      unitId: 15,
    });

    for (const measurement of filteredMeasurements) {
      await observationService.upsertObservation({
        dataLevel: dataLevel,
        entityId: entityId,
        entityExternalId,
        inspectionCategoryId: inspectionCategoryTypeId,
        inspectionId: inspectionId,
        path: path.Measurement,
        value: measurement.value.toString(),
        unitId: 15,
      });
    }

    if (filteredMeasurements.length > 0) {
      const average = calculateAverage(filteredMeasurements);

      await observationService.upsertObservation({
        dataLevel: dataLevel,
        entityId: entityId,
        entityExternalId,
        inspectionCategoryId: inspectionCategoryTypeId,
        inspectionId: inspectionId,
        path: path.CalculatedPitchGauge,
        value: average,
        unitId: 15,
      });
    } else {
      await observationService.deleteObservationsByProps({
        dataLevel: dataLevel,
        entityId: entityId,
        entityExternalId,
        inspectionCategoryId: inspectionCategoryTypeId,
        inspectionId: inspectionId,
        path: path.CalculatedPitchGauge,
        unitId: 15,
      });
    }

    // Same as handleAddMeasurement: direct writes bypass the mutation hooks'
    // invalidation, leaving the Action Status bar on stale observations (#10549).
    await cacheInvalidationService.invalidateInspectionAfterObservationChangeImmediate(
      inspectionId,
    );

    setMeasurements(filteredMeasurements);
  };

  const calculateAverage = (localMeasurements?: Measurement[]): string => {
    return calculateAverageLocal(localMeasurements || measurements);
  };

  const calculateAverageLocal = (measurements: Measurement[]): string => {
    if (measurements.length === 0) return '0';
    const sum = measurements.reduce((acc, curr) => acc + curr.value, 0);
    return (sum / measurements.length).toFixed(2);
  };

  // DISPLAY-ONLY (#6032): show wear extension (average − baseline) instead of
  // the raw absolute average. The stored CalculatedPitchGauge value stays RAW.
  const averageString = (): string => {
    const average = calculateAverage();

    if (average === '0') return 'No measurements';
    return (
      WearDisplayHelpers.formatExtensionDisplayValue({
        average: parseFloat(average),
        dimension: partDimension,
      }) + 'mm'
    );
  };

  const extensionLabel = (): string | null => {
    const average = calculateAverage();
    if (average === '0') return null;
    return WearDisplayHelpers.formatExtensionLabel({
      average: parseFloat(average),
      dimension: partDimension,
    });
  };

  // #10668: what the pending reading WOULD do to the average. Display only —
  // nothing here reaches an observation until handleAddMeasurement runs.
  const pendingPreview = Number.isFinite(pendingNumber)
    ? WearDisplayHelpers.formatPendingPreview({
        committed: measurements.map((measurement) => measurement.value),
        pending: pendingNumber,
        dimension: partDimension,
      })
    : null;

  // Resuming the parked navigation lands on wherever the inspector was heading
  // - a Drawer link goes to that screen, not back one step (#10668).
  const handleLeaveAnyway = () => {
    setLeaveDialogOpen(false);
    setCurrentValue('');
    setError('');
    proceedPendingNavigation();
  };

  const handleStay = () => {
    setLeaveDialogOpen(false);
    cancelPendingNavigation();
  };

  return (
    <div className="py-4">
      <Label tooltipKey="undercarriage-shoes-pitchextension-pitchgauge">
        Measured pitch (pitch gauge)
      </Label>

      {/* #10668: recorded readings come FIRST and are plain text, never inputs.
          The old layout put the entry field on top in an identically-styled
          number input, so a typed-but-not-added value read as a second recorded
          reading and inspectors expected it in the average (see #10665). */}
      <div className="space-y-4 pt-4">
        <div className="space-y-1">
          <div className="text-inkMuted">
            Recorded readings ({measurements.length})
          </div>
          {measurements.length === 0 ? (
            <p className="text-sm text-inkMuted">No readings added yet</p>
          ) : (
            <ul className="divide-y divide-gray-200 rounded-sm border bg-gray-50">
              {measurements.map((measurement, index) => (
                <li
                  key={measurement.id}
                  className="flex items-center gap-3 px-3 py-2"
                >
                  {/* The reading is the record, so it leads at full weight; the
                      counter sits at the same size and is separated by colour
                      alone, so it cannot be misread as part of the measurement
                      while still matching the row's type scale. tabular-nums
                      keeps the decimals aligned down the list. */}
                  <span className="min-w-6 shrink-0 text-inkMuted tabular-nums">
                    {index + 1}.
                  </span>
                  <span className="font-bold text-ink tabular-nums">
                    {measurement.value.toFixed(1)} mm
                  </span>
                  <button
                    onClick={() => handleRemoveMeasurement(measurement.id)}
                    aria-label={`Remove reading ${index + 1}`}
                    className="ml-auto p-1 text-inkMuted hover:text-ink"
                  >
                    <X size={20} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-1">
          <label
            htmlFor={newReadingInputId}
            className="text-base font-normal text-primaryColor"
          >
            New reading
          </label>
          <div className="flex items-center gap-2">
            <input
              id={newReadingInputId}
              type="number"
              value={currentValue}
              onChange={(e) => {
                setCurrentValue(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => {
                // #10668: Enter commits exactly like the button, rejection included
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleAddMeasurement();
                }
              }}
              placeholder="Measurement (mm)"
              className="w-full rounded-sm border-2 border-brand p-2 focus:border-brand focus:ring-2 focus:ring-brand"
            />
            <Tooltip tooltipKey="undercarriage-shoes-pitchextension-pitchgauge-measurement" />
          </div>
          {/* Helper text, not just a placeholder: the unit has to stay readable
              once a value is typed over the placeholder (#10668). */}
          <p className="text-sm text-inkMuted">Measurement (mm)</p>
          {hasPendingReading && (
            <WarningBanner
              showIcon
              message={
                <>
                  Not yet added — tap Add measurement to include{' '}
                  <span className="font-bold">{pendingValue}</span> in the
                  average
                </>
              }
            >
              {pendingPreview && (
                <p className="text-sm">
                  Average would be{' '}
                  <span className="font-bold">{pendingPreview.average}</span>
                  {pendingPreview.extension && (
                    <>
                      , extension{' '}
                      <span className="font-bold">
                        {pendingPreview.extension}
                      </span>
                    </>
                  )}
                </p>
              )}
            </WarningBanner>
          )}
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <button
          onClick={handleAddMeasurement}
          className="flex w-full items-center justify-center gap-2 rounded-sm border border-primaryColor px-4 py-2 text-primaryColor hover:bg-brandLight/30"
        >
          <span className="text-xl">+</span> Add measurement
        </button>
      </div>

      <div className="pt-4">
        <div className="flex flex-col justify-between">
          <div className="text-inkMuted">Calculated pitch gauge extension</div>
          <div className="font-normal">{averageString()}</div>
          {extensionLabel() && (
            <div className="text-sm whitespace-pre-line text-inkMuted">
              {extensionLabel()}
            </div>
          )}
        </div>
      </div>

      {/* #10668: the pending reading must never vanish without the inspector
          knowing. The guard has already parked the exit they attempted, so
          LEAVE ANYWAY drops the value and resumes it. */}
      <CustomDialogBox
        title="Reading not added"
        isOpen={isLeaveDialogOpen}
        description={`${pendingValue} has not been added to the pitch gauge measurements and is not counted in the average. Leave the screen and discard it?`}
        leftButtonText="LEAVE ANYWAY"
        rightButtonText="STAY"
        onLeftClick={handleLeaveAnyway}
        onRightClick={handleStay}
        onBackdropClicked={handleStay}
        // CustomDialogBox always renders its trigger slot and falls back to a
        // stray "Open Dialog" button; an empty div is how OperatingHoursScreen
        // drives it purely from isOpen.
        trigger={<div></div>}
      />
    </div>
  );
};
